import React, { useEffect, useRef, useState } from "react";
import { Wrapper } from "./style";
import Config from "../../utils/config";
import { Heading, ButtonSolid, ButtonOutline } from "../styledConstants";
import MaterialInput from "../common/UI/MaterialInput";
import Request from "../../utils/Request";
import { ToastContainer, toast } from "react-toastify";
import CsvUploadModal from "./UploadCSVModal"; // <- import the new component

export default function AddMCCForm({ setProgramManagerId }) {
  const mccTypes = ["UDYAM", "MUNICIPAL", "GST"];

  const [mccCategories, setMccCategories] = useState([]);
  const [mccSubCategories, setMccSubCategories] = useState([]);
  const [mccCategory, setMccCategory] = useState("");
  const [mccSubCategory, setMccSubCategory] = useState("");
  const [nic, setNic] = useState("");
  const [nicDescription, setNicDescription] = useState("");
  const [hsn, setHsn] = useState("");
  const [hsnDescription, setHsnDescription] = useState("");
  const [mccCode, setMccCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [mccType, setMccType] = useState("");

  // CSV modal state
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);

  useEffect(() => {
    fetchMccGroups();
  }, []);

  // ... (all your existing functions: handleFormSubmit, fetchMccGroups, fetchMccSubCategories, fetchMccDetails)
  // Copy the implementations from your current AddMCCForm code for these functions (unchanged).
  // For brevity here I will keep them — ensure you paste your existing implementations below:
  // handleFormSubmit, fetchMccGroups, fetchMccSubCategories, fetchMccDetails
  // (I'll include them verbatim to ensure the file is ready-to-use)

  const handleFormSubmit = (event) => {
    event.preventDefault();

    const isEmpty = (value) => {
      if (value === null || value === undefined) return true;

      if (typeof value === "object") {
        if (value.value !== undefined) {
          return !value.value || value.value.toString().trim() === "";
        }
        return Object.keys(value).length === 0;
      }

      const stringValue = value.toString().trim();
      return !stringValue || stringValue === "" || ["null", "undefined"].includes(stringValue);
    };

    if (isEmpty(mccType)) {
      toast.error("Please select MCC Type");
      return;
    } else if (isEmpty(mccCategory) || isEmpty(mccSubCategory)) {
      toast.error("Please select both Industry Category and SubCategory");
      return;
    } else if (isEmpty(mccCode)) {
      toast.error("Please enter MCC Code");
      return;
    } else if (mccType === "UDYAM" && (isEmpty(nic) || isEmpty(nicDescription))) {
      toast.error("Please enter both NIC Code and NIC Description");
      return;
    } else if (mccType === "GST" && (isEmpty(hsn) || isEmpty(hsnDescription))) {
      toast.error("Please enter both HSN Code and HSN Description");
      return;
    }

    const payload = {
      industryGroup: mccCategory,
      mccCode: mccCode,
      mccDesc: mccSubCategory,
      mccType: mccType,
    };

    if (mccType === "UDYAM") {
      payload.nic = nic;
      payload.nicDesc = nicDescription;
    } else if (mccType === "GST") {
      payload.hsn = hsn;
      payload.hsnDesc = hsnDescription;
    }

    const successHandler = (res) => {
      console.log("res::: ", res);
      setLoading(false);
      if (res?.code === "0000" || res?.status === 200) {
        toast.success(`MCC (${mccType}) saved successfully`);
        // Reset form
        setMccCategory("");
        setMccSubCategory("");
        setMccType("");
        setMccCode("");
        setNic("");
        setNicDescription("");
        setHsn("");
        setHsnDescription("");
      } else {
        toast.error(res?.msg || res?.message || "Something went wrong");
      }
    };

    const errorHandler = (err) => {
      console.log("err:: ", err);
      setLoading(false);
      toast.error("Error saving MCC details");
      console.error("Error: ", err);
    };

    setLoading(true);
    const api = new Request("", successHandler, errorHandler, false);
    api.post(`${Config.apis.user.ADD_NEW_MCC}`, payload);
  };

  const fetchMccGroups = () => {
    const successHandler = (res) => {
      console.log("res: ", res);
      setLoading(false);
      if (res?.data) {
        setMccCategories(res?.data || []);
      }
    };
    const errorHandler = () => {
      setLoading(false);
    };
    setLoading(true);
    const api = new Request("", successHandler, errorHandler, false);
    return api.get(`${Config.apis.user.MCC_GROUPS}`);
  };

  const fetchMccSubCategories = (value) => {
    const successHandler = (res) => {
      setLoading(false);
      if (res?.data) {
        setMccSubCategories(res?.data || []);
      }
    };
    const errorHandler = () => {
      setLoading(false);
    };
    setLoading(true);
    const api = new Request("", successHandler, errorHandler, false);
    return api.get(`${Config.apis.user.MCC_SUB_CATEGORY}?industryGroup=${value}`);
  };

  const fetchMccDetails = (value) => {
    const successHandler = (res) => {
      setLoading(false);
      if (res?.data) {
        setMccCode(res?.data?.mccCode);
        setNic(res?.data?.nic);
        setNicDescription(res?.data?.nicDesc);
      }
    };
    const errorHandler = () => {
      setLoading(false);
    };
    setLoading(true);
    const api = new Request("", successHandler, errorHandler, false);
    return api.get(`${Config.apis.user.MCC_DETAILS}?industryGroup=${mccCategory}&mccDesc=${value}`);
  };

  // handle callback after successful CSV upload
  const onCsvUploaded = (res) => {
    // example: refresh categories after successful upload
    fetchMccGroups();
  };

  return (
    <Wrapper>
      <div className="flex items-center space-between mb10 mt32">
        <Heading size="xxl">Add New Mcc</Heading>

        {/* Upload CSV button aligned right */}
        <div>
          <ButtonOutline
            rg
            className="flex flex-center"
            primary
            onClick={() => setIsCsvModalOpen(true)}
            disabled={csvUploading}
            aria-haspopup="dialog"
          >
            Upload CSV
          </ButtonOutline>
        </div>
      </div>

      <form className="mt32" onSubmit={handleFormSubmit}>
        {/* ... your existing form markup (unchanged) ... */}
        <div className="form-wrapper">
          <div className="form-inner">
            <div className="flex flex-center mt10 mb20">
              <MaterialInput
                name="Type of MCC"
                value={mccType ? { value: mccType, label: mccType } : null}
                type="createSelect"
                placeholder="Select MCC Type"
                options={[
                  { value: "", label: "Select MCC Type" },
                  ...mccTypes.map((type) => ({ value: type, label: type })),
                ]}
                onChange={(selectedOption) => {
                  setMccType(selectedOption.value);
                  setNic("");
                  setNicDescription("");
                  setHsn("");
                  setHsnDescription("");
                }}
              />
            </div>

            {mccType === "UDYAM" && (
              <>
                <div className="flex flex-center mb20">
                  <MaterialInput
                    name="nicValue"
                    value={nic}
                    className="item-text"
                    type="text"
                    placeholder="NIC Value"
                    onChange={(e) => setNic(e.target.value)}
                  />
                </div>
                <div className="flex flex-center mb20">
                  <MaterialInput
                    name="nicDescription"
                    value={nicDescription}
                    className="item-text"
                    type="text"
                    placeholder="NIC Description"
                    onChange={(e) => setNicDescription(e.target.value)}
                  />
                </div>
              </>
            )}

            {mccType === "GST" && (
              <>
                <div className="flex flex-center mb20">
                  <MaterialInput
                    name="hsnValue"
                    value={hsn}
                    className="item-text"
                    type="text"
                    placeholder="HSN Value"
                    onChange={(e) => setHsn(e.target.value)}
                  />
                </div>
                <div className="flex flex-center mb20">
                  <MaterialInput
                    name="hsnDescription"
                    value={hsnDescription}
                    className="item-text"
                    type="text"
                    placeholder="HSN Description"
                    onChange={(e) => setHsnDescription(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="flex flex-center mt10 mb20">
              <MaterialInput
                name="industryCategory"
                value={mccCategory ? { value: mccCategory, label: mccCategory } : null}
                type="createSelect"
                placeholder="Select Industry Category"
                options={[
                  { value: "", label: "Select Industry Category" },
                  ...mccCategories.map((category) => ({ value: category, label: category })),
                ]}
                onChange={(selectedOption) => {
                  setMccCategory(selectedOption?.value || "");
                  setMccSubCategory("");
                  setMccCode("");
                  fetchMccSubCategories(selectedOption?.value);
                }}
              />
            </div>

            <div className="flex flex-center mt10 mb20">
              <MaterialInput
                name="industrySubCategory"
                value={mccSubCategory ? { value: mccSubCategory, label: mccSubCategory } : null}
                type="createSelect"
                placeholder="Select Industry SubCategory"
                options={[
                  { value: "", label: "Select Industry SubCategory" },
                  ...mccSubCategories.map((category) => ({ value: category, label: category })),
                ]}
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption?.value || "";
                  setMccSubCategory(selectedValue);
                  fetchMccDetails(selectedValue);
                }}
              />
            </div>

            <div className="flex flex-center mt10 mb20">
              <MaterialInput
                name="mccCode"
                value={mccCode || ""}
                className="item-text"
                type="text"
                placeholder="MCC Code"
                onChange={(e) => setMccCode(e.target.value)}
              />
            </div>

            <ButtonSolid width="320px" primary xl type="submit" className="mt20">
              CREATE
            </ButtonSolid>
          </div>
        </div>
      </form>

      <CsvUploadModal
        isOpen={isCsvModalOpen}
        onClose={() => setIsCsvModalOpen(false)}
        onUploaded={onCsvUploaded}
        uploadEndpoint={Config.apis?.user?.UPLOAD_MCC_CSV}
      />

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </Wrapper>
  );
}
